import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { Cart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    shipTo: ShipToModel;
    pickUpWarehouse?: WarehouseModel | null;
    extendedStyles?: OrderConfirmationShippingInformationStyles;
    billTo:BillToModel;
}

export interface OrderConfirmationShippingInformationStyles {
    shippingInformationGridContainer?: GridContainerProps;
    shippingInformationTitleGridItem?: GridItemProps;
    shippingInformationTitle?: TypographyProps;
    shippingInformationAddressGridItem?: GridItemProps;
    shippingAddressTitle?: TypographyProps;
    shippingAddress?: AddressInfoDisplayStyles;
    shippingOtherInformationGridItem?: GridItemProps;
    shippingOtherInformationGridContainer?: GridContainerProps;
    shippingInformationCarrierGridItem?: GridItemProps;
    shippingCarrierTitle?: TypographyProps;
    shippingCarrierDescription?: TypographyProps;
    shippingInformationDateRequestedGridItem?: GridItemProps;
    shippingDateRequestedTitle?: TypographyProps;
    shippingDateRequestedText?: TypographyProps;
    shippingInformationServiceGridItem?: GridItemProps;
    shippingServiceTitle?: TypographyProps;
    shippingServiceDescription?: TypographyProps;
    shippingInformationNumberGridItem?: GridItemProps;
    shippingNumberTitle?: TypographyProps;
    shippingNumberText?: TypographyProps;
    billingInformationTitleGridItem?: GridItemProps;
    billingInformationTitle?: TypographyProps;
    billingInformationBillingAddressGridItem?: GridItemProps;
}

export const orderConfirmationShippingInformationStyles: OrderConfirmationShippingInformationStyles = {
    shippingInformationGridContainer: {
        gap: 10,
        css: css`
            padding-bottom: 0;
        `,
    },
    shippingInformationTitleGridItem: {
        width: 12,
    },
    shippingInformationTitle: {
        variant: "h5",
        as: "h2",
        css: css`
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: 1.375rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 1.4375rem;
            width: 100%;
            padding-top: 0;
            margin-bottom: .625rem;
        `,
    },
    shippingInformationAddressGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            color: #333F48;
            font-family: Lato;
            font-size: 1rem !important;
            letter-spacing: 0;
            line-height: 1.5rem;
            padding-left: .31250625rem;
            &.carrier-title {
                padding-bottom: 0;
                flex-basis: 100%;
                max-width: 100%;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [   
                        css`
                            padding-bottom: .9375rem;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            };
        `,
    },
    shippingAddressTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                display: inline-block;
            }
            margin-bottom: .31250625rem;
        `,
    },
    shippingOtherInformationGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            padding-left: 0;
        `,
    },
    shippingOtherInformationGridContainer: {
        gap: 10,
        css: css`
            padding-left: .31250625rem;
        `,
    },
    shippingInformationCarrierGridItem: {
        css: css`
            flex-direction: row;
            padding-bottom: 0;
            padding-top: 0;
            padding-left: .31250625rem;

            > div {
                color: #333F48;
                font-family: Lato;
                font-size: 1rem !important;
                letter-spacing: 0;
                line-height: 1.5rem;
            }
        `,
        width: 12,
    },
    shippingCarrierDescription: {
        css: css`
            color: #333F48;
            font-family: Lato;
            font-size: 1rem !important;
            letter-spacing: 0;
            line-height: 1.5rem;
            @media print {
                color: #333F48;
                font-family: Lato;
                font-size: 1rem !important;
                letter-spacing: 0;
                line-height: 1.5rem;
            }
        `,
    },
    shippingCarrierTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: 1.375rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 1.4375rem;
            width: 100%;
            margin-bottom: .625rem;
        `,
    },
    shippingInformationDateRequestedGridItem: {
        width: 12,
        css: css`
            flex-direction: row;
        `,
    },
    shippingDateRequestedTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            line-height: 1;
            margin-right: .31250625rem;
            @media print {
                display: inline-block;
            }
            margin-bottom: .31250625rem;
        `,
    },
    shippingInformationServiceGridItem: {
        css: css`
            flex-direction: row;
            line-height: 1;
            padding-bottom: 0;
            color: #333F48;
            font-family: Lato;
            font-size: 1rem !important;
            letter-spacing: 0;
            line-height: 1.125rem;
        `,
        width: 12,
    },
    shippingServiceTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            margin-right: .31250625rem;
            margin-bottom: .125rem;
            @media print {
                display: inline-block;
            }
        `,
    },
    shippingInformationNumberGridItem: {
        css: css`
            flex-direction: row;
            line-height: 1;
            padding-bottom: 0;
            color: #333F48;
            font-family: Lato;
            font-size: 1rem !important;
            letter-spacing: 0;
            line-height: 1.125rem;
        `,
        width: 12,
    },
    shippingNumberTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            margin-right: .31250625rem;
            margin-bottom: .125rem;
            @media print {
                display: inline-block;
            }
        `,
    },
    shippingNumberText: {
        css: css`
            line-height: 1;
            color: #333F48;
            font-family: Lato;
            font-size: 1rem !important;
            letter-spacing: 0;
            line-height: 1.125rem;
        `,
    },
    billingInformationTitleGridItem: {
        width: 12,
    },
    billingInformationTitle: {
        variant: "h5",
        as: "h2",
        css: css`
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: 1.375rem;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 1.4375rem;
            padding-top: 0;
            margin-bottom: .625rem;
        `,
    },
    billingInformationBillingAddressGridItem: {
        width: [12, 12, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
            color: #333F48;
            font-family: Lato;
            font-size: 1rem;
            letter-spacing: 0;
            line-height: 1.5rem;
            padding-left: 0;
        `,
    },
};

type Props = OwnProps;

const OrderConfirmationShippingInformation: FC<Props> = ({ cart, shipTo, pickUpWarehouse, extendedStyles,billTo }) => {
    const [styles] = useState(() => mergeToNew(orderConfirmationShippingInformationStyles, extendedStyles));
    return (
        <GridContainer
            {...styles.shippingInformationGridContainer}
            data-test-selector="orderConfirmationShippingInformation"
        >
            {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                <GridItem {...styles.shippingInformationAddressGridItem}>
                    <Typography {...styles.shippingInformationTitle}>{translate("Shipping Information")}</Typography>                    <AddressInfoDisplay
                        companyName={shipTo.companyName}
                        address1={shipTo.address1}
                        address2={shipTo.address2}
                        address3={shipTo.address3}
                        address4={shipTo.address4}
                        city={shipTo.city}
                        postalCode={shipTo.postalCode}
                        state={shipTo.state ? shipTo.state.abbreviation : undefined}
                        country={shipTo.country ? shipTo.country.abbreviation : undefined}
                        extendedStyles={styles.shippingAddress}
                    />
                </GridItem>
            )}
             <GridItem {...styles.shippingInformationAddressGridItem}>
                    <Typography {...styles.shippingInformationTitle}>{translate("Billing Information")}</Typography>                    <AddressInfoDisplay
                        companyName={billTo.companyName}
                        address1={billTo.address1}
                        address2={billTo.address2}
                        address3={billTo.address3}
                        address4={billTo.address4}
                        city={billTo.city}
                        postalCode={billTo.postalCode}
                        state={billTo.state ? billTo.state.abbreviation : undefined}
                        country={billTo.country ? billTo.country.abbreviation : undefined}
                        extendedStyles={styles.shippingAddress}
                    />
                </GridItem>
            {cart.fulfillmentMethod === FulfillmentMethod.PickUp && pickUpWarehouse && (
                <GridItem {...styles.shippingInformationAddressGridItem}>
                    <Typography {...styles.shippingAddressTitle}>{translate("Pick Up Location")}</Typography>
                    <AddressInfoDisplay
                        {...pickUpWarehouse}
                        companyName={pickUpWarehouse.description || pickUpWarehouse.name}
                        extendedStyles={styles.shippingAddress}
                    />
                </GridItem> 
            )}
            <GridItem {...styles.shippingOtherInformationGridItem}>
                <GridContainer {...styles.shippingOtherInformationGridContainer}>
                    {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                        <GridItem {...styles.shippingInformationAddressGridItem} className="carrier-title">
                            <Typography {...styles.shippingCarrierTitle}>{translate("Carrier")}</Typography>
                        </GridItem>
                    )}
                    {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                        <GridItem {...styles.shippingInformationCarrierGridItem}>
                            <Typography {...styles.shippingCarrierDescription}>{cart.carrier!.description}</Typography>
                        </GridItem>
                    )}
                    {(cart.requestedDeliveryDate || cart.requestedPickupDate) && (
                        <GridItem {...styles.shippingInformationDateRequestedGridItem}>
                            <Typography {...styles.shippingDateRequestedTitle}>
                                {translate("Date Requested:")}
                            </Typography>
                            <Typography
                                {...styles.shippingDateRequestedText}
                                data-test-selector="orderConfirmation_requestedDate"
                            >
                                <LocalizedDateTime
                                    dateTime={cart.requestedDeliveryDateDisplay || cart.requestedPickupDateDisplay}
                                />
                            </Typography>
                        </GridItem>
                    )}
                    {cart.fulfillmentMethod === FulfillmentMethod.Ship && (
                        <GridItem {...styles.shippingInformationServiceGridItem}>
                            <Typography {...styles.shippingServiceDescription}>{cart.shipVia?.description}</Typography>
                        </GridItem>
                    )}
                    {cart?.carrier && cart?.carrier.description?.toLowerCase().endsWith("collect")&&(
                        <GridItem {...styles.shippingInformationNumberGridItem}>
                            <Typography {...styles.shippingNumberTitle}>{translate("Account Number:")}</Typography>
                            <Typography {...styles.shippingNumberText}>{cart?.properties?.customerFreightBillingAcctNumber}</Typography>
                        </GridItem>
                    )}
                    
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default OrderConfirmationShippingInformation;
